/* istanbul ignore file */
import { faBath } from '@fortawesome/pro-light-svg-icons/faBath';
import { faBedAlt } from '@fortawesome/pro-light-svg-icons/faBedAlt';
import { faCouch } from '@fortawesome/pro-light-svg-icons/faCouch';
import { faEnvelope } from '@fortawesome/pro-regular-svg-icons/faEnvelope';
import { faPhone } from '@fortawesome/pro-regular-svg-icons/faPhone';
import { getAgentLogos, getThumbImages } from '@propertypal/shared/src/utils/property';
import getSellingAgent from '@propertypal/shared/src/utils/property/getSellingAgent';
import React, { FunctionComponent, MouseEventHandler } from 'react';
import { useTheme } from 'styled-components';
import FontAwesomeIcon from '../icons/FontAwesomeIcon';
import LazyImage from '../image/LazyImage';
import PropertyPrice from '../property/PropertyPrice';
import { SubHeading, Text } from '../typography';
import EnergyRatingIcon from './EnergyRatingIcon';
import OpenViewingOverlay from './OpenViewingOverlay';
import {
  Container,
  Content,
  LinkContainer,
  TextBox,
  ThumbImage,
  ThumbImageContainer,
  SummaryRow,
  ExtraDescription,
  OptionRow,
  TextContent,
  Footer,
  ImageContent,
  TextBoxSide,
  TextBoxMain,
} from './PremierBox.style';
import PropertyBadge from './PropertyBadge';
import PropertyBanner from './PropertyBanner';
import { BottomRow, ContactText } from './PropertyBox.style';
import { AgentLogo } from './PropertyBoxFooter.style';
import PropertyBoxSlider from './PropertyBoxSlider';
import PropertyFavourite from './PropertyFavourite';
import PropertyHide from './PropertyHide';
import { Props } from './SearchPropertyBox';

const PremierBox: FunctionComponent<Props> = (props) => {
  const { property } = props;
  const theme = useTheme();
  const {
    srpBrandingBgColour,
    srpBrandingTextColour,
    pdpBrandingLinkColour,
    pdpBrandingButtonBgColour,
    pdpBrandingButtonTextColour,
  } = props.property.enhancedBrandingColours || {};
  const thumbImages = getThumbImages(property, 3, props.thumbImageLocator);
  const canHide = props.onHideToggle && property.type === 'property';
  const canFavourite = props.onFavourite && property.type === 'property';
  const sellingAgent = getSellingAgent(props.property);
  const accountLogos = getAgentLogos(property);

  const handleEmailClick: MouseEventHandler = (e) => {
    if (props.onEmailClick) {
      e.preventDefault();
      props.onEmailClick(property);
    }
  };

  const handleCallClick: MouseEventHandler = (e) => {
    if (props.onCallClick) {
      e.preventDefault();
      props.onCallClick(property);
    }
  };

  return (
    <Container>
      <LinkContainer
        href={props.href || property.path}
        prefetch={false}
        onClick={props.onClick}
        $borderColor={srpBrandingBgColour}
        draggable={false}
      >
        <Content>
          <ImageContent>
            <PropertyBoxSlider
              property={props.property}
              imageLocator={props.imageLocator}
              useSlider={props.useSlider}
            />

            <PropertyBanner property={property} />

            {property.openViewing && !property.openViewing.past && (
              <OpenViewingOverlay
                openViewing={property.openViewing}
                themeName="dark"
                containerStyle={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: 0,
                }}
              />
            )}
          </ImageContent>

          <TextBox>
            <TextContent>
              <TextBoxMain>
                <div>
                  <PropertyBadge property={property} useWideDisplay />

                  <SubHeading
                    color={theme.textMid}
                    fontSize={21}
                    mb={12}
                    fontWeight="600"
                    data-testid="property-box-address"
                  >
                    {property.displayAddress.replace(/\s+/g, ' ')}
                  </SubHeading>

                  <PropertyPrice
                    containerStyle={{ marginBottom: 10, fontWeight: 600 }}
                    price={property.price}
                    alternativeCurrencyPrice={property.alternativeCurrencyPrice}
                    fontSize={34}
                    status={property.status}
                    comingSoonText={property.comingSoonText}
                    prefixBreak
                    primaryColor={theme.textDark}
                    prefixColor={theme.textDark}
                    suffixColor={theme.textDark}
                  />

                  {!!props.property.briefText && (
                    <Text fontSize={16} color={theme.textLight} mb={10}>
                      {props.property.briefText}
                    </Text>
                  )}

                  <SummaryRow>
                    {!!property.numBedrooms && (
                      <>
                        <FontAwesomeIcon icon={faBedAlt} color={theme.textDark} size={22} />
                        <Text color={theme.textLight}>{property.numBedrooms}</Text>
                      </>
                    )}

                    {!!property.numBathrooms && (
                      <>
                        <FontAwesomeIcon icon={faBath} color={theme.textDark} size={22} />
                        <Text color={theme.textLight}>{property.numBathrooms}</Text>
                      </>
                    )}

                    {!!property.numReceptionRooms && (
                      <>
                        <FontAwesomeIcon icon={faCouch} color={theme.textDark} size={22} />
                        <Text color={theme.textLight}>{property.numReceptionRooms}</Text>
                      </>
                    )}

                    {property.ber && <EnergyRatingIcon property={property} hideEpcText />}
                  </SummaryRow>

                  {props.property.premierHighlight && (
                    <ExtraDescription
                      data-testid="propertyDescription"
                      $color={
                        props.property.enhancedBrandingColours
                          ? props.property.enhancedBrandingColours.pdpBrandingLinkColour
                          : theme.textDark
                      }
                    >
                      {props.property.premierHighlight}
                    </ExtraDescription>
                  )}
                </div>

                {(props.onCallClick || props.onEmailClick) && (
                  <div>
                    <ContactText
                      style={{ backgroundColor: pdpBrandingButtonBgColour, color: pdpBrandingButtonTextColour }}
                    >
                      Contact {sellingAgent?.organisation}
                    </ContactText>

                    <BottomRow $color={pdpBrandingLinkColour}>
                      {props.onCallClick && (
                        <button type="button" onClick={handleCallClick} data-testid="propertyBoxCall">
                          <FontAwesomeIcon icon={faPhone} size={22} style={{ color: pdpBrandingLinkColour }} />
                          Call
                        </button>
                      )}

                      {props.onEmailClick && (
                        <button type="button" onClick={handleEmailClick} data-testid="propertyBoxEmail">
                          <FontAwesomeIcon
                            icon={faEnvelope}
                            size={22}
                            style={{ marginTop: 1, color: pdpBrandingLinkColour }}
                          />
                          Email
                        </button>
                      )}
                    </BottomRow>
                  </div>
                )}
              </TextBoxMain>

              <TextBoxSide>
                <OptionRow>
                  {canHide && (
                    <PropertyHide
                      hidden={property.hidden}
                      onHideToggle={(hidden) => props.onHideToggle!()(hidden, property.id)}
                      // unique key needed in infinite grid, once item above is removed from list
                      // it would inherit its hidden state value in PropertyHide
                      key={`hide-${property.id}`}
                      showLabel
                      color={pdpBrandingLinkColour}
                    />
                  )}
                  {canFavourite && (
                    <PropertyFavourite
                      favourited={property.shortlisted}
                      // @ts-ignore
                      onFavourite={(favourited) => props.onFavourite()(favourited, property.id)}
                      // unique key needed in infinite grid, once item above is removed from list
                      // it would inherit its favourited state value in PropertyFavourite
                      key={property.id}
                      showLabel
                      containerStyle={{ marginLeft: 10 }}
                      color={pdpBrandingLinkColour}
                    />
                  )}
                </OptionRow>
              </TextBoxSide>
            </TextContent>

            <ThumbImageContainer>
              {thumbImages &&
                thumbImages.map((thumbImage, index) => (
                  <ThumbImage key={thumbImage.url}>
                    <LazyImage
                      src={thumbImage.url}
                      blurHash={thumbImage.blurHash}
                      alt={`${property.displayAddress} photo ${index + 2}`}
                      aria-hidden="true"
                      width={340}
                      height={255}
                      loading={props.loading}
                    />
                  </ThumbImage>
                ))}
            </ThumbImageContainer>
          </TextBox>
        </Content>

        <Footer color={srpBrandingBgColour}>
          {!!sellingAgent && (
            <Text fontSize={12} color={srpBrandingTextColour} style={{ flex: 1 }}>
              Marketed by {sellingAgent.organisation}
            </Text>
          )}

          {accountLogos &&
            accountLogos.length > 0 &&
            accountLogos.map((logo) => (
              <AgentLogo
                src={logo.url}
                key={logo.url}
                alt={`${sellingAgent?.organisation} Logo`}
                aria-hidden="true"
                loading="lazy"
              />
            ))}
        </Footer>
      </LinkContainer>
    </Container>
  );
};

export default PremierBox;
